import {
    ComponentType,
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AddObservationsDrawer, { AddObservationsDrawerProps } from './AddObservationsDrawer';
import AddTaskNoteDrawer, { AddTaskNoteDrawerProps } from './AddTaskNoteDrawer';
import CareConfirmationDrawer, { CareConfirmationDrawerProps } from './CareConfirmationDrawer';
import CompleteInsightDrawer, { CompleteInsightDrawerProps } from './CompleteInsightDrawer';
import ComputerDevicePhoneVerificationDrawer from './ComputerDevicePhoneVerificationDrawer';
import ConfirmationDrawer, { ConfirmationDrawerProps } from './ConfirmationDrawer';
import DetectReviewOutcomeDrawer, {
    DetectReviewOutcomeDrawerProps,
} from './DetectReviewOutcomeDrawer';
import DeviceResetDrawer, { DeviceResetDrawerProps } from './DeviceResetDrawer';
import DoorScheduleDrawer, { DoorScheduleDrawerProps } from './DoorScheduleDrawer';
import DrawerContainer from './DrawerContainer';
import ExploreInsightsDrawer from './ExploreInsightsDrawer';
import VerifyPhoneNumberDrawer from './PhoneNumberVerificationFlow/VerifyPhoneNumberDrawer';
import PhoneSubscriptionDrawer, { PhoneSubscriptionDrawerProps } from './PhoneSubscriptionDrawer';
import ReceiveHighFiveDrawer, { ReceiveHighFiveDrawerProps } from './ReceiveHighFiveDrawer';
import ResidentDetailsDrawer, { ResidentDetailsDrawerProps } from './ResidentDetailsDrawer';
import ResidentPendantsDrawer, { ResidentPendantsDrawerProps } from './ResidentPendantsDrawer';
import ResolveTaskDrawer, { ResolveTaskDrawerProps } from './ResolveTaskDrawer';
import SelectSectionGgResolutionDrawer, {
    SelectSectionGgResolutionDrawerProps,
} from './SectionGgResolutionFlow/SelectSectionGgResolutionDrawer';
import SelectCareTasksAndOutcomesDrawer, {
    SelectCareTasksAndOutcomesDrawerProps,
} from './SelectCareTasksAndOutcomesDrawer';
import SelectCareZoneDrawer from './SelectCareZoneDrawer';
import SelectDeviceDrawer from './SelectDeviceDrawer';
import SelectDoorDrawer, { SelectDoorDrawerProps } from './SelectDoorDrawer';
import SelectEmergencyReasonDrawer, {
    SelectEmergencyReasonDrawerProps,
} from './SelectEmergencyReasonDrawer';
import SelectEnterpriseDrawer, { SelectEnterpriseDrawerProps } from './SelectEnterpriseDrawer';
import SelectMultiOutcomeResolutionDrawer, {
    SelectMultiOutcomeResolutionDrawerProps,
} from './SelectMultiOutcomeResolutionDrawer';
import SelectOptionsDrawer, { SelectOptionsDrawerProps } from './SelectOptionsDrawer';
import SelectPersonDrawer, { SelectPersonDrawerProps } from './SelectPersonDrawer';
import SelectProactiveResolutionDrawer, {
    SelectProactiveResolutionDrawerProps,
} from './SelectProactiveResolutionDrawer';
import SelectResidentsWithButtonsDrawer, {
    SelectResidentsWithButtonsDrawerProps,
} from './SelectResidentsWithButtonsDrawer';
import SelectRoomCheckinDrawer, { SelectRoomCheckinDrawerProps } from './SelectRoomCheckinDrawer';
import SelectShiftDrawer from './SelectShiftDrawer';
import SelectUnitDrawer, { SelectUnitDrawerProps } from './SelectUnitDrawer';
import SelectWardDrawer, { SelectWardDrawerProps } from './SelectWardDrawer';
import SelectWasFallDrawer from './SelectWasFallDrawer';
import SendHighFiveDrawer, { SendHighFiveDrawerProps } from './SendHighFiveDrawer';
import ShiftDriftDrawer from './ShiftDriftDrawer';
import StaffMemberActionsDrawer, {
    StaffMemberActionsDrawerProps,
} from './StaffMemberActionsDrawer';
import UnitCheckInDrawer, { UnitCheckInDrawerProps } from './UnitCheckInDrawer';
import UnitDetailDrawer, { UnitDetailDrawerProps } from './UnitDetailDrawer';
import omit from 'lodash/omit';
import { useAnalytics } from 'src/AnalyticsContext';
import { Drawers } from 'src/constants/Drawers';
import useBlockPageScroll from 'src/hooks/useBlockPageScroll';

export type DrawerPropsMap = {
    [Drawers.AddTaskNote]: AddTaskNoteDrawerProps;
    [Drawers.CareConfirmation]: CareConfirmationDrawerProps;
    [Drawers.CompleteInsight]: CompleteInsightDrawerProps;
    [Drawers.DeviceReset]: DeviceResetDrawerProps;
    [Drawers.DoorScheduleDrawer]: DoorScheduleDrawerProps;
    [Drawers.ExploreInsights]: DrawerComponentProps;
    [Drawers.PhoneSubscription]: PhoneSubscriptionDrawerProps;
    [Drawers.ReceiveHighFive]: ReceiveHighFiveDrawerProps;
    [Drawers.ResolveTask]: ResolveTaskDrawerProps;
    [Drawers.SelectCareTasksAndOutcomes]: SelectCareTasksAndOutcomesDrawerProps;
    [Drawers.SelectCareZone]: DrawerComponentProps;
    [Drawers.ComputerDevicePhoneVerification]: DrawerComponentProps;
    [Drawers.SelectDevice]: DrawerComponentProps;
    [Drawers.SelectEmergencyReason]: SelectEmergencyReasonDrawerProps;
    [Drawers.SelectEnterprise]: SelectEnterpriseDrawerProps;
    [Drawers.SelectMultiOutcomeResolution]: SelectMultiOutcomeResolutionDrawerProps;
    [Drawers.SelectProactiveResolution]: SelectProactiveResolutionDrawerProps;
    [Drawers.SelectSectionGgResolution]: SelectSectionGgResolutionDrawerProps;
    [Drawers.SelectShift]: DrawerComponentProps;
    [Drawers.ShiftDrift]: DrawerComponentProps;
    [Drawers.AddObservations]: AddObservationsDrawerProps;
    [Drawers.SelectResidentsWithButtons]: SelectResidentsWithButtonsDrawerProps;
    [Drawers.SelectRoomCheckin]: SelectRoomCheckinDrawerProps;
    [Drawers.SelectWasFall]: DrawerComponentProps;
    [Drawers.SendHighFive]: SendHighFiveDrawerProps;
    [Drawers.StaffMemberActionsDrawer]: StaffMemberActionsDrawerProps;
    [Drawers.UnitCheckIn]: UnitCheckInDrawerProps;
    [Drawers.UnitDetail]: UnitDetailDrawerProps;
    [Drawers.SelectDoor]: SelectDoorDrawerProps;
    [Drawers.ResidentPendants]: ResidentPendantsDrawerProps;
    [Drawers.Confirmation]: ConfirmationDrawerProps;
    [Drawers.DetectReviewOutcome]: DetectReviewOutcomeDrawerProps;
    [Drawers.ResidentDetails]: ResidentDetailsDrawerProps;
    [Drawers.SelectOptions]: SelectOptionsDrawerProps;
    [Drawers.SelectPerson]: SelectPersonDrawerProps;
    [Drawers.SelectUnit]: SelectUnitDrawerProps;
    [Drawers.SelectWard]: SelectWardDrawerProps;
    [Drawers.VerifyPhoneNumber]: DrawerComponentProps;
};

export type SupportedDrawer = keyof DrawerPropsMap;

// Union type for arrays containing different drawer types
export type DrawerTypeArray = DrawerType<SupportedDrawer>[];

export type DrawerContextValuesType = {
    openDrawer: <T extends SupportedDrawer>(
        drawer: DrawerType<T>,
        handleOnComplete?: Function,
        handleOnCancel?: Function
    ) => void;
    openDrawerWorkflow: (
        drawers: DrawerTypeArray,
        handleOnComplete?: Function,
        handleOnCancel?: Function
    ) => void;
    closeDrawer: () => void;
    isDrawerOpen: boolean;
};

export type DrawerSpecificProps<T extends keyof DrawerPropsMap> = Omit<
    DrawerPropsMap[T],
    'hasPreviousDrawer' | 'hasNextDrawer' | 'onClose' | 'onNext' | 'onPrevious'
>;

// Generic drawer type that maps drawer types to their specific props
export type DrawerType<T extends SupportedDrawer = SupportedDrawer> = {
    type: T;
    priority?: number;
    disableCloseOnBackdropClick?: boolean;
    disableCloseOnEscapeKeyDown?: boolean;
    fullHeight?: boolean;
    showCloseIcon?: boolean;
    props?: DrawerSpecificProps<T>;
};

interface DrawerManagerProps {
    children: React.ReactNode;
}

export type OnClose = (event?: InputEvent | {}, reason?: string) => void;

/**
 * We aren't adding type safety to the OnNext and onPrevious handling, since
 * then we'd have to add every prop type to essentially every drawer.
 * What we miss out on in terms of prop type safety, we gain in the flexibility
 * to string together drawers conditionally without being overly verbose within each drawer :)
 */

export type OnNext = (payload?: {}, nextDrawer?: DrawerType<any>) => void;
export type OnPrevious = (addCurrentToFuture?: boolean, propsToOmit?: string[]) => void;

export interface DrawerComponentProps {
    hasPreviousDrawer: boolean;
    hasNextDrawer: boolean;
    onClose: OnClose;
    onNext: OnNext;
    onPrevious: OnPrevious;
    title?: string;
    showCancelAction?: boolean;
}

const DrawersMap: {
    [key: string]: ((props: any) => ReactNode) | undefined;
} = {
    [Drawers.AddTaskNote]: AddTaskNoteDrawer,
    [Drawers.CareConfirmation]: CareConfirmationDrawer,
    [Drawers.Confirmation]: ConfirmationDrawer,
    [Drawers.DeviceReset]: DeviceResetDrawer,
    [Drawers.DetectReviewOutcome]: DetectReviewOutcomeDrawer,
    [Drawers.CompleteInsight]: CompleteInsightDrawer,
    [Drawers.DoorScheduleDrawer]: DoorScheduleDrawer,
    [Drawers.ExploreInsights]: ExploreInsightsDrawer,
    [Drawers.PhoneSubscription]: PhoneSubscriptionDrawer,
    [Drawers.ReceiveHighFive]: ReceiveHighFiveDrawer,
    [Drawers.ResolveTask]: ResolveTaskDrawer,
    [Drawers.SelectCareTasksAndOutcomes]: SelectCareTasksAndOutcomesDrawer,
    [Drawers.SelectCareZone]: SelectCareZoneDrawer,
    [Drawers.ComputerDevicePhoneVerification]: ComputerDevicePhoneVerificationDrawer,
    [Drawers.SelectDevice]: SelectDeviceDrawer,
    [Drawers.SelectEmergencyReason]: SelectEmergencyReasonDrawer,
    [Drawers.SelectEnterprise]: SelectEnterpriseDrawer,
    [Drawers.SelectMultiOutcomeResolution]: SelectMultiOutcomeResolutionDrawer,
    [Drawers.SelectProactiveResolution]: SelectProactiveResolutionDrawer,
    [Drawers.SelectSectionGgResolution]: SelectSectionGgResolutionDrawer,
    [Drawers.SelectShift]: SelectShiftDrawer,
    [Drawers.ShiftDrift]: ShiftDriftDrawer,
    [Drawers.AddObservations]: AddObservationsDrawer,
    [Drawers.SelectOptions]: SelectOptionsDrawer,
    [Drawers.SelectPerson]: SelectPersonDrawer,
    [Drawers.SelectResidentsWithButtons]: SelectResidentsWithButtonsDrawer,
    [Drawers.SelectRoomCheckin]: SelectRoomCheckinDrawer,
    [Drawers.SelectWasFall]: SelectWasFallDrawer,
    [Drawers.SendHighFive]: SendHighFiveDrawer,
    [Drawers.StaffMemberActionsDrawer]: StaffMemberActionsDrawer,
    [Drawers.UnitCheckIn]: UnitCheckInDrawer,
    [Drawers.UnitDetail]: UnitDetailDrawer,
    [Drawers.SelectDoor]: SelectDoorDrawer,
    [Drawers.SelectWard]: SelectWardDrawer,
    [Drawers.SelectUnit]: SelectUnitDrawer,
    [Drawers.ResidentPendants]: ResidentPendantsDrawer,
    [Drawers.ResidentDetails]: ResidentDetailsDrawer,
    [Drawers.VerifyPhoneNumber]: VerifyPhoneNumberDrawer,
};

export const DrawerContext = createContext({
    openDrawer: <T extends SupportedDrawer>(
        _drawer: DrawerType<T>,
        _handleOnComplete?: Function,
        _handleOnCancel?: Function
    ) => {},
    openDrawerWorkflow: (
        _drawers: DrawerTypeArray,
        _handleOnComplete?: Function,
        _handleOnCancel?: Function
    ) => {},
    closeDrawer: () => {},
    isDrawerOpen: false,
});

function DrawerManager(props: DrawerManagerProps) {
    const { children } = props;

    const { palette, spacing } = useTheme();

    const { trackEvent } = useAnalytics();

    const [currentDrawer, setCurrentDrawer] = useState<DrawerType<SupportedDrawer> | null>();
    const [expectedDrawer, setExpectedDrawer] = useState<DrawerType<SupportedDrawer> | null>();
    const [isOpen, setIsOpen] = useState(false);

    const updatePageScrollBlock = useBlockPageScroll();

    const past = useRef<DrawerTypeArray>([]);
    const future = useRef<DrawerTypeArray>([]);

    const previousOnCompleteWorkflow = useRef<Function | null>(null);
    const onCompleteWorkflow = useRef<Function | undefined>(undefined);
    const onCompletePayload = useRef<{} | undefined>(undefined);
    const onCancelWorkflow = useRef<Function | undefined>(undefined);
    const isCancelled = useRef(false);

    const disableCloseOnBackdropClick = useRef(false);
    const disableCloseOnEscapeKeyDown = useRef(true);

    useEffect(() => {
        if (isOpen) {
            updatePageScrollBlock(true, window.scrollY);
        } else {
            updatePageScrollBlock(false, window.scrollY);
        }
    }, [isOpen, updatePageScrollBlock]);

    const resetDrawerState = () => {
        setCurrentDrawer(null);
        setExpectedDrawer(null);
        setIsOpen(false);
        previousOnCompleteWorkflow.current = null;
        onCancelWorkflow.current = undefined;
        onCompleteWorkflow.current = undefined;
        onCompletePayload.current = undefined;
        isCancelled.current = false;
        past.current = [];
        future.current = [];
        disableCloseOnBackdropClick.current = false;
        disableCloseOnEscapeKeyDown.current = true;
    };

    const handleCloseDrawer: OnClose = useCallback(
        async (event, reason) => {
            // Guard against double-close: during the exit animation (150ms) the backdrop
            // remains interactive and can fire onClose a second time. Without this, a
            // second call mid-transition would clear expectedDrawer and silently abort
            // the workflow. More likely when throttling CPU or on Sage managed phones when the animation window widens.
            if (!isOpen) {
                return;
            }

            if (
                (!!disableCloseOnBackdropClick.current && reason === 'backdropClick') ||
                (!!disableCloseOnEscapeKeyDown.current && reason === 'escapeKeyDown')
            ) {
                return;
            }

            trackEvent('Drawer Closed', { drawerType: currentDrawer?.type, reason });

            if (onCancelWorkflow.current) {
                try {
                    await onCancelWorkflow.current?.(currentDrawer?.props || {});
                } catch (error: any) {
                    datadogLogs.logger.error(
                        'Error cancelling drawer workflow',
                        undefined,
                        error as Error
                    );
                } finally {
                    onCancelWorkflow.current = undefined;
                }
            }
            isCancelled.current = true;
            setExpectedDrawer(null);
            setIsOpen(false);
        },
        [currentDrawer, isOpen, trackEvent]
    );

    const handleOpenDrawer = useCallback(
        <T extends SupportedDrawer>(
            drawer: DrawerType<T>,
            onComplete?: Function,
            onCancel?: Function
        ) => {
            const {
                disableCloseOnBackdropClick: shouldDisableBackdropClickClose = false,
                disableCloseOnEscapeKeyDown: shouldDisableEscapeKeyClose = true,
                priority = 0,
            } = drawer || {};

            if (!!DrawersMap[drawer?.type]) {
                if ((currentDrawer?.priority || 0) > priority) {
                    // If there's a currently opened drawer with higher priority, queue the next drawer
                    datadogLogs.logger.info('Queuing drawer', {
                        currentDrawer: {
                            type: currentDrawer?.type,
                            priority: currentDrawer?.priority,
                        },
                        queuedDrawer: { type: drawer?.type, priority },
                    });
                    future.current.unshift(drawer);
                } else {
                    disableCloseOnBackdropClick.current = !!shouldDisableBackdropClickClose;
                    disableCloseOnEscapeKeyDown.current = !!shouldDisableEscapeKeyClose;

                    setCurrentDrawer(drawer);

                    if (onCompleteWorkflow.current) {
                        previousOnCompleteWorkflow.current = onCompleteWorkflow.current;
                    }
                    onCompleteWorkflow.current = onComplete;
                    onCancelWorkflow.current = onCancel;
                    setIsOpen(true);
                }
            } else {
                datadogLogs.logger.warn(`Drawer type ${drawer?.type} is not supported`);
                resetDrawerState();
            }
        },
        [currentDrawer]
    );

    /**
     * Begins a drawer workflow and opens the first drawer in the provided list
     */
    const handleOpenDrawerWorkflow = useCallback(
        (drawers: DrawerTypeArray = [], onComplete?: Function, onCancel?: Function) => {
            if (drawers?.length > 0) {
                const drawer = drawers?.[0];
                future.current = drawers.slice(1);
                handleOpenDrawer(drawer, onComplete, onCancel);
            } else {
                datadogLogs.logger.warn('No drawers passed into workflow');
                resetDrawerState();
            }
        },
        [handleOpenDrawer]
    );

    /**
     * Closes the current drawer and opens the previous drawer in the workflow. If there are no previous drawers,
     * the workflow is completed and the drawer is closed.
     */
    const handlePreviousDrawer: OnPrevious = (addCurrentToFuture = true, propsToOmit) => {
        if (past.current?.length > 0) {
            const drawer = past.current.pop();
            setExpectedDrawer(drawer);
            setIsOpen(false);
            if (addCurrentToFuture) {
                const drawerPropsToKeep = propsToOmit
                    ? omit(currentDrawer?.props || {}, propsToOmit)
                    : currentDrawer?.props || {};
                if (currentDrawer) {
                    future.current.unshift({ ...currentDrawer, props: drawerPropsToKeep });
                }
            } else {
                if (previousOnCompleteWorkflow.current) {
                    onCompleteWorkflow.current = previousOnCompleteWorkflow.current;
                    previousOnCompleteWorkflow.current = null;
                }
            }
        } else {
            console.warn('No previous drawers');
            handleCloseDrawer();
        }
    };

    /**
     * If specified, opens the next drawer, otherwise takes the next drawer from the provided workflow.
     * If there are no next drawers, completes the workflow and closes the drawer.
     *
     * Only properties inside "prop" will be persisted from the current to the next drawer
     */
    const handleNextDrawer: OnNext = (payload = {}, nextDrawer?: DrawerType) => {
        // Pick out properties that should not be carried over to the next drawer
        const {
            title: _unused_title,
            showCancelAction: _unused_show_cancel_action,
            ...propsToKeep
        } = currentDrawer?.props || {};

        const nextDrawerProps = nextDrawer?.props || {};

        if (nextDrawer && nextDrawer.type) {
            setExpectedDrawer({
                ...nextDrawer,
                props: { ...nextDrawerProps, ...propsToKeep, ...payload },
            });
        } else if (future.current?.length > 0) {
            const next = future.current?.shift?.();

            if (next) {
                const {
                    disableCloseOnBackdropClick: shouldDisableBackdropClickClose,
                    disableCloseOnEscapeKeyDown: shouldDisableEscapeKeyClose,
                    props: nextDrawerProps,
                    ...restDrawerConfigs
                } = next || {};

                if (shouldDisableBackdropClickClose === undefined) {
                    disableCloseOnBackdropClick.current = !!shouldDisableBackdropClickClose;
                }
                if (shouldDisableEscapeKeyClose === undefined) {
                    disableCloseOnEscapeKeyDown.current = !!shouldDisableEscapeKeyClose;
                }

                // Note: the order of the prop spread here is to maintain any user-specified props from future drawers (e.g., title).
                // However, this can have the unintended consequence of overriding props from the current drawer (e.g., resolutionType).
                // TODO: consider a better way to handle this
                setExpectedDrawer({
                    ...restDrawerConfigs,
                    type: next.type,
                    props: { ...propsToKeep, ...payload, ...nextDrawerProps },
                });
            }
        } else {
            onCompletePayload.current = { ...propsToKeep, ...payload };
        }

        if (currentDrawer) {
            past.current.push(currentDrawer);
        }
        setIsOpen(false);
    };

    const handleDrawerEntered = () => {
        if (expectedDrawer) {
            setCurrentDrawer(expectedDrawer);
            setExpectedDrawer(null);
        }
    };

    const handleDrawerExited = useCallback(async () => {
        if (expectedDrawer) {
            setCurrentDrawer(expectedDrawer);
            setExpectedDrawer(null);
            setIsOpen(true);
        } else {
            const completeWorkflow = onCompleteWorkflow.current;
            const completePayload = onCompletePayload.current;
            const cancelled = isCancelled.current;
            resetDrawerState();
            if (!cancelled) {
                try {
                    await completeWorkflow?.(completePayload);
                } catch (error: any) {
                    datadogLogs.logger.error(
                        'Error completing drawer workflow',
                        undefined,
                        error as Error
                    );
                }
            }
        }
    }, [expectedDrawer]);

    const DrawerComponent: ComponentType<any> | undefined = currentDrawer
        ? (DrawersMap[currentDrawer.type] as ComponentType<any>)
        : undefined;
    const { fullHeight = false, showCloseIcon = false, props: drawerProps } = currentDrawer || {};

    const drawerContextValues: DrawerContextValuesType = useMemo(
        () => ({
            openDrawer: handleOpenDrawer,
            openDrawerWorkflow: handleOpenDrawerWorkflow,
            closeDrawer: handleCloseDrawer,
            isDrawerOpen: isOpen,
        }),
        [handleCloseDrawer, handleOpenDrawer, handleOpenDrawerWorkflow, isOpen]
    );

    return (
        <DrawerContext.Provider value={drawerContextValues}>
            <>
                {children}
                <DrawerContainer
                    aria-modal
                    anchor="bottom"
                    fullHeight={fullHeight}
                    onClose={handleCloseDrawer}
                    open={isOpen}
                    slotProps={{
                        transition: {
                            onEntered: handleDrawerEntered,
                            onExited: handleDrawerExited,
                            timeout: { enter: 250, exit: 150 },
                        },
                    }}
                >
                    {DrawerComponent && (
                        <DrawerComponent
                            {...drawerProps}
                            hasPreviousDrawer={!!(past.current?.length > 0)}
                            hasNextDrawer={!!(future.current?.length > 0)}
                            onClose={handleCloseDrawer}
                            onNext={handleNextDrawer}
                            onPrevious={handlePreviousDrawer}
                        />
                    )}

                    {showCloseIcon && (
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseDrawer}
                            size="medium"
                            disableRipple
                            sx={{
                                position: 'absolute',
                                top: spacing(2.5),
                                right: spacing(2.5),
                                padding: 0,
                            }}
                        >
                            <FontAwesomeIcon icon={faTimes} color={palette.gray?.[900]} />
                        </IconButton>
                    )}
                </DrawerContainer>
            </>
        </DrawerContext.Provider>
    );
}

export function useDrawerManager(): DrawerContextValuesType {
    const context = useContext(DrawerContext);
    if (!context) {
        throw new Error('useDrawerManager must be used within a DrawerManager');
    }
    return context;
}

export default DrawerManager;
